import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"

import { Button } from "components"
import { thousandSeparator } from "utils"
import LfsSpecificationsGroup from "./LfsSpecificationsGroup"
import {
  sanitizeDemoMediaUrl,
  getSelectedItemsBySpecificationId,
  getSelectedSpecificationTotal,
  isCustomBuilderStepValid
} from "./customBuilder"
import classes from "./KioskCustomBuilder.module.scss"

const StepMedia = ({ step }) => {
  const videoRef = useRef(null)

  const mediaUrl =
    sanitizeDemoMediaUrl(step?.mediaUrl)

  useEffect(() => {
    if (step?.mediaType !== "video" || !mediaUrl || !videoRef.current) return

    const videoElement = videoRef.current

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          videoElement.play().catch(() => {})

          return
        }

        videoElement.pause()
      },
      {
        threshold: 0.5
      }
    )

    observer.observe(videoElement)

    return () => {
      observer.disconnect()
      videoElement.pause()
    }
  }, [mediaUrl, step?.mediaType])

  if (!mediaUrl || step?.mediaType === "none") return null

  const alt =
    step.mediaAlt || step.title || ""

  return (
    <div className={classes["media-card"]}>
      {step.mediaType === "video"
        ? <video
            ref={videoRef}
            src={mediaUrl}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            controls={false}
          />
        : <img
            src={mediaUrl}
            alt={alt}
          />
      }
    </div>
  )
}

const KioskCustomBuilder = (props) => {
  const {
    food,
    selectedOption,
    selectedSpecificationItems,
    setSelectedSpecificationItems,
    onComplete
  } = props

  const foodSpecificationsMap =
    useSelector(state => state.foodSpecificationsMap)

  const [currentStepIndex, setCurrentStepIndex] = useState(0)

  const customBuilder =
    food.customBuilder

  const specificationMap = useMemo(() => {
    const map = {}

    food.specificationIds.forEach(specificationId => {
      const specification =
        foodSpecificationsMap[specificationId]

      if (!specification) return

      map[specification._id] = specification
    })

    return map
  }, [food.specificationIds, foodSpecificationsMap])

  const steps =
    [...(customBuilder?.steps || [])]
      .sort((a, b) => (a.order || 0) - (b.order || 0))

  const currentStep =
    steps[currentStepIndex]

  const currentSpecification =
    currentStep?.specificationId
      ? specificationMap[currentStep.specificationId]
      : null

  const currentStepValid =
    currentStep
      ? isCustomBuilderStepValid(
          currentStep,
          selectedSpecificationItems
        )
      : true

  const totalSpecifications =
    getSelectedSpecificationTotal(selectedSpecificationItems)

  const totalPrice =
    (selectedOption?.salePrice || 0) +
    totalSpecifications

  useEffect(() => {
    if (selectedSpecificationItems.length === 0) {
      setCurrentStepIndex(0)
    }
  }, [food._id, selectedOption?._id, selectedSpecificationItems.length])

  const handleGoNext = event => {
    if (!currentStepValid) return

    if (currentStepIndex === steps.length - 1) {
      if (onComplete) {
        onComplete(event)
      }

      return
    }

    setCurrentStepIndex(prev =>
      Math.min(prev + 1, steps.length - 1)
    )
  }

  const handleGoBack = () => {
    setCurrentStepIndex(prev =>
      Math.max(prev - 1, 0)
    )
  }

  if (steps.length === 0) return null

  return (
    <div className={classes["container"]}>
      <div className={classes["header"]}>
        <div>
          <h3 className={classes["title"]}>
            {customBuilder.title || food.name}
          </h3>

          <p className={classes["subtitle"]}>
            مرحله {currentStepIndex + 1} از {steps.length}
          </p>
        </div>

        <div className={classes["price-box"]}>
          <span className={classes["price-label"]}>
            قیمت فعلی
          </span>

          <strong className={classes["price-value"]}>
            {thousandSeparator(totalPrice)}
          </strong>
        </div>
      </div>

      <div className={classes["stepper"]}>
        {steps.map((step, index) =>
          <button
            key={step._id || `${step.key}-${index}`}
            type="button"
            className={classes[
              index === currentStepIndex
                ? "step-active"
                : isCustomBuilderStepValid(step, selectedSpecificationItems)
                  ? "step-done"
                  : "step"
            ]}
            disabled={index > currentStepIndex && !currentStepValid}
            onClick={() => index <= currentStepIndex && setCurrentStepIndex(index)}
          >
            <span className={classes["step-number"]}>
              {index + 1}
            </span>

            <span className={classes["step-title"]}>
              {step.title}
            </span>
          </button>
        )}
      </div>

      {currentStep?.uiMode === "summary"
        ? <div className={classes["summary-card"]}>
            {steps
              .filter(step => step.uiMode !== "summary")
              .map(step => {
                const selectedItems =
                  getSelectedItemsBySpecificationId(
                    selectedSpecificationItems,
                    step.specificationId,
                    step.key
                  )

                return (
                  <div
                    key={step._id || step.key}
                    className={classes["summary-group"]}
                  >
                    <div className={classes["summary-group-title"]}>
                      {step.title}
                    </div>

                    {selectedItems.length > 0
                      ? selectedItems.map(item =>
                          <div
                            key={`${step.key}-${item._id}`}
                            className={classes["summary-item"]}
                          >
                            <span>
                              {item.name}
                              {(item.quantity || 1) > 1 ? ` x${item.quantity}` : ""}
                            </span>

                            <span>
                              {item.salePrice > 0
                                ? thousandSeparator(
                                    item.salePrice *
                                    ((item.quantity || 1) - (item.freeQuantity || 0))
                                  )
                                : "رایگان"
                              }
                            </span>
                          </div>
                        )
                      : <div className={classes["summary-empty"]}>
                          انتخاب نشده
                        </div>
                    }
                  </div>
                )
              })}

            <div className={classes["summary-total"]}>
              <span>جمع کل</span>
              <strong>{thousandSeparator(totalPrice)}</strong>
            </div>

            <div className={classes["summary-help"]}>
              مراحل کامل شد. برای ثبت سفارش، دکمه تکمیل مراحل را بزنید.
            </div>
          </div>
        : <div className={classes["step-layout"]}>
            <StepMedia step={currentStep} />

            <div className={classes["step-card"]}>
              <div className={classes["step-card-header"]}>
                <div>
                  <div className={classes["step-card-title"]}>
                    {currentStep?.title}
                  </div>

                  <div className={classes["step-card-note"]}>
                    {currentStep?.isRequired
                      ? "تکمیل این مرحله الزامی است"
                      : "این مرحله اختیاری است"
                    }
                  </div>
                </div>
              </div>

              {!!currentSpecification &&
                <LfsSpecificationsGroup
                  specification={currentSpecification}
                  maxSelect={currentStep.maxSelect}
                  selectedSpecificationItems={selectedSpecificationItems}
                setSelectedSpecificationItems={setSelectedSpecificationItems}
                builderStepKey={currentStep.key}
                portion={currentStep.portion || "whole"}
                />
              }
            </div>
          </div>
      }

      <div className={classes["footer"]}>
        <Button
          variant="outline"
          onClick={handleGoBack}
          disabled={currentStepIndex === 0}
        >
          مرحله قبل
        </Button>

        <Button
          onClick={handleGoNext}
          disabled={!currentStepValid}
        >
          {currentStepIndex === steps.length - 1 ? "تکمیل مراحل" : "مرحله بعد"}
        </Button>
      </div>
    </div>
  )
}

export default React.memo(KioskCustomBuilder)
